import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { useQuery } from '@apollo/client/react';
import { getAccessToken, isAuthenticated, refreshAccessToken } from '@/api/auth';
import { Button } from '@/components/ui/Button';
import { SessionContext, type SessionContextValue } from './session-context';
import { MeDoc } from './meDocument';
import { isPublicPathname } from './publicRoutes';

/**
 * Silent session restore on a private page. `idle` and `refreshing` both hold
 * the session in `loading` (no redirect); `done` means Go answered (a session,
 * or 401/403 meaning none); `unreachable` means every bounded retry failed on a
 * network / 5xx error, which shows a retry panel instead of bouncing to /login.
 */
type RestoreState = 'idle' | 'refreshing' | 'done' | 'unreachable';

const RESTORE_ATTEMPTS = 3;
const RESTORE_BACKOFF_MS = [500, 1500];

/**
 * Loads the authenticated user once for the whole app and exposes it through
 * `useSession()`. A 401 from the resolver is redirected to /login by the
 * Apollo error link (see apolloClient.ts); pages therefore only need to worry
 * about the loaded / loading states here.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const isPublic = isPublicPathname(pathname);
  // The access token lives in memory only, so a cold load (or a first private
  // navigation after a public page) has none until the HttpOnly refresh cookie
  // has been exchanged for one.
  const hasSession = isAuthenticated();
  const [restore, setRestore] = useState<RestoreState>('idle');
  const [retryKey, setRetryKey] = useState(0);
  const [wasPublic, setWasPublic] = useState(isPublic);
  if (isPublic !== wasPublic) {
    // Crossing between public and private routes without a token: forget an
    // earlier 'done' (a 401) so the silent refresh runs again before
    // ProtectedRoute decides, e.g. after signing in from another tab.
    setWasPublic(isPublic);
    if (!getAccessToken()) setRestore('idle');
  }

  useEffect(() => {
    if (isPublic || getAccessToken()) {
      return;
    }
    let cancelled = false;
    (async () => {
      setRestore('refreshing');
      for (let attempt = 0; attempt < RESTORE_ATTEMPTS; attempt += 1) {
        try {
          await refreshAccessToken();
          if (!cancelled) setRestore('done');
          return;
        } catch {
          // Network or 5xx, not a 401: the cookie is still valid, so retry a
          // bounded number of times before giving up with a retry panel.
          if (attempt < RESTORE_ATTEMPTS - 1) {
            await new Promise((resolve) => setTimeout(resolve, RESTORE_BACKOFF_MS[attempt]));
          }
          if (cancelled) return;
        }
      }
      if (!cancelled) setRestore('unreachable');
    })();
    return () => {
      cancelled = true;
    };
  }, [isPublic, retryKey]);

  const { data, previousData, loading, refetch } = useQuery(MeDoc, {
    skip: isPublic || !hasSession,
    // Me is session identity, not a live grid. cache-and-network (the app
    // default) refetches on every observer and blanks the sidebar to
    // Profile-only while permissions reload (~2s on client navigation).
    fetchPolicy: 'cache-first',
  });

  const me = data?.me ?? previousData?.me ?? null;

  const value = useMemo<SessionContextValue>(
    () => ({
      me,
      loading: isPublic
        ? false
        : hasSession
          ? loading && !me
          : restore !== 'done',
      refresh: () => refetch(),
    }),
    [me, loading, refetch, isPublic, hasSession, restore],
  );

  if (!isPublic && !hasSession && restore === 'unreachable') {
    return (
      <SessionContext.Provider value={value}>
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-900">
          <div role="alert" className="max-w-sm space-y-4 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Cannot reach the server. Your session is still active; check your connection and try again.
            </p>
            <Button
              onClick={() => {
                setRestore('idle');
                setRetryKey((key) => key + 1);
              }}
            >
              Try again
            </Button>
          </div>
        </div>
      </SessionContext.Provider>
    );
  }

  return (
    <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
  );
}
